import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPrint, faExpand, faCompress, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import type { InventoryUnit } from '../data/inventory';
import './MonthlyInventoryReportModal.css';

function escapeCsvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function escapeHtml(v: string): string {
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildCsv(headers: string[], rows: string[][]): string {
  const lines = [headers.map(escapeCsvCell).join(',')];
  for (const row of rows) {
    lines.push(row.map(escapeCsvCell).join(','));
  }
  return lines.join('\r\n');
}

/** Format an ISO date (YYYY-MM-DD) without timezone drift; pass through anything else. */
function formatInstallationDate(raw?: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const [, y, mo, d] = m;
    return `${Number(mo)}/${Number(d)}/${y}`;
  }
  return s;
}

const REPORT_HEADERS = [
  'City',
  'Business',
  'Product type',
  'Serial number',
  'Status',
  'Condition',
  'Assembled',
  'Installation date',
];

function unitToCells(u: InventoryUnit): string[] {
  return [
    u.location ?? '',
    u.business ?? '',
    u.productType ?? '',
    u.serialNumber ?? '',
    u.status ?? '',
    u.condition ?? '',
    u.assembled ? 'Yes' : 'No',
    formatInstallationDate(u.installationDate),
  ];
}

export interface ProductUnitsReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Report title, e.g. "TIM-E Inventory Report". */
  title: string;
  /** Units belonging to the corresponding product. */
  units: InventoryUnit[];
}

/**
 * Inventory units table for a single product (TIM-E / BIM-E), with an added
 * Installation date column. Supports sort, print, CSV download, full screen,
 * and open in a new tab, mirroring the monthly inventory report.
 */
const ProductUnitsReportModal: React.FC<ProductUnitsReportModalProps> = ({ isOpen, onClose, title, units }) => {
  const rows = useMemo(() => units.map(unitToCells), [units]);

  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [fullScreen, setFullScreen] = useState(false);

  const toggleSort = useCallback(
    (col: number) => {
      if (sortCol === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortCol(col);
        setSortDir('asc');
      }
    },
    [sortCol]
  );

  const sortedRows = useMemo(() => {
    if (sortCol === null) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const ca = a[sortCol] ?? '';
      const cb = b[sortCol] ?? '';
      return ca.localeCompare(cb, undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [rows, sortCol, sortDir]);

  const handleClose = useCallback(() => {
    setFullScreen(false);
    setSortCol(null);
    setSortDir('asc');
    onClose();
  }, [onClose]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const buildReportHtml = useCallback(() => {
    const headRow = REPORT_HEADERS.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
    const bodyRows = rows
      .map((cells) => `<tr>${cells.map((c) => `<td>${escapeHtml(c || '—')}</td>`).join('')}</tr>`)
      .join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 24px; color: #1f2933; }
  h1 { font-size: 1.5rem; margin: 0 0 1rem; }
  table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  th, td { border: 1px solid #d2d6dc; padding: 6px 8px; text-align: left; }
  thead th { background: #f5f7fa; position: sticky; top: 0; }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<table>
<thead><tr>${headRow}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
</body>
</html>`;
  }, [rows, title]);

  const handleOpenInNewTab = useCallback(() => {
    const html = buildReportHtml();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      URL.revokeObjectURL(url);
      alert('Please allow pop-ups to open the report in a new tab.');
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, [buildReportHtml]);

  const handleDownloadCsv = useCallback(() => {
    const csv = buildCsv(REPORT_HEADERS, rows);
    const safeFile = title.replace(/[/\\?%*:|"<>]/g, '-');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeFile}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, title]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      extraWide={!fullScreen}
      fullScreen={fullScreen}
      contentClassName="inventory-monthly-report-print-scope"
    >
      <div className="modal-body inventory-monthly-report-body">
        <p className="inventory-monthly-report-lead">
          Inventory units for this product, including the installation date for each unit.
        </p>
        <div className="inventory-report-toolbar no-print">
          <button type="button" className="page-toolbar-btn" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.35rem' }} aria-hidden />
            Print
          </button>
          <button type="button" className="page-toolbar-btn" onClick={handleDownloadCsv}>
            <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.35rem' }} aria-hidden />
            Download
          </button>
          <button
            type="button"
            className="page-toolbar-btn"
            onClick={() => setFullScreen((v) => !v)}
            aria-pressed={fullScreen}
          >
            <FontAwesomeIcon icon={fullScreen ? faCompress : faExpand} style={{ marginRight: '0.35rem' }} aria-hidden />
            {fullScreen ? 'Exit full screen' : 'Full screen'}
          </button>
          <button type="button" className="page-toolbar-btn" onClick={handleOpenInNewTab}>
            <FontAwesomeIcon icon={faUpRightFromSquare} style={{ marginRight: '0.35rem' }} aria-hidden />
            Open in new tab
          </button>
        </div>
        <div className="inventory-monthly-report-table-wrap">
          <table className="inventory-monthly-report-table">
            <thead>
              <tr>
                {REPORT_HEADERS.map((h, idx) => (
                  <th
                    key={h}
                    className="inventory-monthly-report-sortable-th"
                    onClick={() => toggleSort(idx)}
                    aria-sort={sortCol === idx ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {h}
                    <span className="inventory-monthly-report-sort-indicator" aria-hidden>
                      {sortCol === idx ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={REPORT_HEADERS.length}>No inventory units for this product.</td>
                </tr>
              ) : (
                sortedRows.map((cells, i) => (
                  <tr key={i}>
                    {cells.map((cell, j) => (
                      <td key={j}>{cell || '—'}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="modal-actions no-print">
        <button type="button" className="cancel-button" onClick={handleClose}>
          Close
        </button>
      </div>
    </Modal>
  );
};

export default ProductUnitsReportModal;
